import React, { Component } from 'react';
import Icons from '@touchsource/tsicons';
import { getAccountFeatureGroups } from '@touchsource/wayfinding_lib';
import { envENV } from '../ENV';

// Returns { [settingUUID]: { name, type } } so flattenGraphicData can iterate
// only the setting.<uuid> keys present on a single graphic.
function buildSettingMap(flatData) {
	const map = {};
	for (const key in flatData) {
		if (!key.startsWith('setting.')) continue;
		const rec = flatData[key];
		if (!rec || !rec.name) continue;
		let type = '';
		for (const k in rec) {
			if (k.startsWith('setting_type.')) {
				type = flatData[k]?.type || '';
				break;
			}
		}
		if (type) map[key] = { name: rec.name, type };
	}
	return map;
}

// Mirrors the wayfinding lib's ETL setEtlDataFromArray, scoped to one graphic
// — needed because the lib's ETL only runs after WayfindingComponent mounts.
function flattenGraphicData(graphicData, settingMap) {
	if (!graphicData) return null;
	const flat = {};
	for (const k in graphicData) {
		if (!k.startsWith('setting.')) {
			flat[k] = graphicData[k];
			continue;
		}
		const entry = settingMap[k];
		if (!entry) continue;
		const sv = graphicData[k];
		const val = sv?.[entry.type];
		if (val !== undefined && val !== null && val !== '') {
			flat[entry.name] = val;
		}
	}
	return flat;
}

// Returns { [mapKey]: [pointKey, ...] } — the shape wayfindToFeatureGroupPoint
// expects. wayfinding_lib's getFeatureGroupMaps collapses to map keys only.
export function buildFeatureGroupCandidatesByMap(flatData, featureGroupKey) {
	if (!flatData || !featureGroupKey) return {};

	const result = {};
	for (const key in flatData) {
		if (!key.startsWith('wf_map_graphic.')) continue;
		const mapPointsPath = flatData[key]?.map_points;
		if (!mapPointsPath || !flatData[mapPointsPath]) continue;

		const mapPoints = flatData[mapPointsPath];
		for (const pointKey in mapPoints) {
			if (!pointKey.startsWith('wf_point.')) continue;
			const pointData = flatData[pointKey];
			if (pointData && pointData[featureGroupKey]) {
				if (!result[key]) result[key] = [];
				result[key].push(pointKey);
			}
		}
	}
	return result;
}

// Module-level singleton claim — guards against duplicate bars when multiple
// Navigation components have building_features_navigation enabled.
let claimed = false;

function toClassSuffix(text) {
	return String(text || '')
		.toLowerCase()
		.split(/[^a-z0-9]+/)
		.filter(Boolean)
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join('') || 'Feature';
}

class BuildingFeatures extends Component {
	constructor(props) {
		super(props);
		this.state = {
			// Pulled from the per-theme buildingFeatures.module.css loaded by
			// App.js's importCSSModules under the `BuildingFeatures` key.
			// Falls back to an empty object so className lookups don't NPE on
			// the (unexpected) case of the loader not having populated it yet.
			css: props.CSSModules?.BuildingFeatures || {},
			activeButton: '',
			active: false,
		};
	}

	componentDidMount() {
		if (!claimed) {
			claimed = true;
			this.isOwner = true;
			this.setState({ active: true });
		}
	}

	componentWillUnmount() {
		if (this.isOwner) claimed = false;
	}

	handleDown = (key) => {
		this.setState({ activeButton: key });
	}

	handleUp = () => {
		if (this.state.activeButton !== '') {
			this.setState({ activeButton: '' });
		}
	}

	handleClick = (featureGroupKey) => {
		this.props.navEvents('openFeatureGroupWayfindingModal', featureGroupKey);
	}

	renderIcon = (featureGroup, graphicKey, graphicDataRaw, settingMap) => {
		const { css } = this.state;
		const graphicData = flattenGraphicData(graphicDataRaw, settingMap) || {};

		if (graphicKey && graphicKey.startsWith('wf_graphic_icon.')) {
			return (
				<div className={`buildingFeaturesIcon ${css.buildingFeaturesIcon || ''}`}>
					<Icons
						alt={featureGroup.display_name || 'Feature'}
						value={graphicData.value || graphicData.label || ''}
					/>
				</div>
			);
		}

		if (graphicKey && graphicKey.startsWith('wf_graphic_image.') && graphicData?.value) {
			return (
				<div className={`buildingFeaturesImage ${css.buildingFeaturesImage || ''}`}>
					<img
						src={`https://ts-wayfinding.s3.amazonaws.com/${envENV}/${graphicData.value}`}
						alt={featureGroup.display_name || 'Feature'}
						draggable="false"
					/>
				</div>
			);
		}

		return null;
	}

	render() {
		if (!this.state.active) return null;

		const flatData = window.flatData;
		if (!flatData) return null;

		const featureGroups = getAccountFeatureGroups(flatData);
		const featureGroupKeys = Object.keys(featureGroups);
		if (featureGroupKeys.length === 0) return null;

		// Drop FGs that bronco's LEFT JOIN backfilled with the empty-uuid stub,
		// and FGs the builder has disabled for wayfinding navigation.
		const renderableKeys = featureGroupKeys.filter(fgKey => {
			const points = featureGroups[fgKey]?.points || [];
			const hasPoint = points.some(p => p && p.uuid);
			return hasPoint && featureGroups[fgKey]?.nav_enabled !== false;
		});
		if (renderableKeys.length === 0) return null;

		const settingMap = buildSettingMap(flatData);

		const { css, activeButton } = this.state;
		const adaClass = this.props.adaActive === true ? `adaActive ${css.adaActive || ''}` : '';

		return (
			<div className={`buildingFeaturesContainer ${css.buildingFeaturesContainer || ''} ${adaClass}`}>
				{renderableKeys.map((fgKey) => {
					const fg = featureGroups[fgKey];
					const activeClass = activeButton === fgKey ? `activeButton ${css.activeButton || ''}` : '';

					const accessibleName = fg.display_name || fg.graphicData?.value || fg.graphicData?.label || 'feature';
					const buttonSlugClass = `buildingFeaturesButton${toClassSuffix(fg.display_name)}`;
					return (
						<button
							key={fgKey}
							type="button"
							aria-label={accessibleName}
							className={`buildingFeaturesButton ${buttonSlugClass} ${css.buildingFeaturesButton || ''} ${activeClass}`}
							onMouseDown={() => { this.handleDown(fgKey); }}
							onMouseUp={this.handleUp}
							onMouseLeave={this.handleUp}
							onTouchStart={() => { this.handleDown(fgKey); }}
							onTouchEnd={this.handleUp}
							onPointerDown={() => { this.handleDown(fgKey); }}
							onPointerUp={this.handleUp}
							onPointerLeave={this.handleUp}
							onClick={() => { this.handleClick(fgKey); }}
						>
							{this.renderIcon(fg, fg.graphic, fg.graphicData, settingMap)}
							<div className={`buildingFeaturesLabel ${css.buildingFeaturesLabel || ''}`}>
								{fg.display_name || ''}
							</div>
						</button>
					);
				})}
			</div>
		);
	}
}

export default BuildingFeatures;
